import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shopService } from "../services/shopService";
import {
  ArrowLeftIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import { baseURL } from "../../../lib/axios";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Number(n ?? 0));
const fcfa = (n) => `${fmt(Math.round(Number(n ?? 0)))} F`;
const date = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "–");

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-medium text-gray-900">{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 text-right">{value ?? "–"}</span>
    </div>
  );
}

const statusMeta = {
  pending: { t: "En attente", c: "bg-amber-50 text-amber-700" },
  approved: { t: "Ouvert", c: "bg-green-50 text-green-700" },
  rejected: { t: "Rejeté", c: "bg-red-50 text-red-700" },
  closed: { t: "Fermée", c: "bg-gray-100 text-gray-600" },
};

const ShopDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shop", id],
    queryFn: () => shopService.getShopDetails(id),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: () => shopService.toggleShopStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["shop", id]);
      queryClient.invalidateQueries(["shops"]);
      setShowStatusModal(false);
    },
  });

  const toggleVerificationMutation = useMutation({
    mutationFn: () => shopService.toggleShopVerification(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["shop", id]);
      queryClient.invalidateQueries(["shops"]);
      setShowVerificationModal(false);
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="p-4 text-sm text-red-500">
        Erreur lors du chargement de la boutique.
      </div>
    );

  const { shop, statistics } = data;
  const isActive = shop.status === "approved";
  const sm = statusMeta[shop.status] || statusMeta.closed;
  const owner = `${shop.user?.firstname || ""} ${shop.user?.lastname || ""}`.trim();
  const rating = Number(shop.ratings_avg_rating ?? shop.average_rating ?? 0);

  return (
    <div className="p-5">
      <button
        onClick={() => navigate("/shops")}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 cursor-pointer"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Retour aux boutiques
      </button>

      {/* En-tête */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 flex items-center gap-4">
        {shop.logo ? (
          <img
            src={`${baseURL}/storage/${shop.logo}`}
            alt=""
            className="h-14 w-14 rounded-full object-cover"
          />
        ) : (
          <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center">
            <ShoppingBagIcon className="h-7 w-7 text-gray-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-medium text-gray-900 truncate">
              {shop.name}
            </h1>
            {shop.is_verified && (
              <ShieldCheckIcon className="h-5 w-5 text-blue-500" title="Certifiée" />
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${sm.c}`}>
              {sm.t}
            </span>
            <span className="text-xs text-gray-400">Créée le {date(shop.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowVerificationModal(true)}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            {shop.is_verified ? "Retirer certif." : "Certifier"}
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            className={`px-3 py-1.5 text-sm rounded-md text-white cursor-pointer ${
              isActive ? "bg-red-500 hover:bg-red-600" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {isActive ? "Fermer" : shop.status === "pending" ? "Approuver" : "Réactiver"}
          </button>
        </div>
      </div>

      {/* Statistiques — chiffres simples, sans graphe */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <Stat label="Produits" value={fmt(shop.products_count)} />
        <Stat label="Commandes" value={fmt(shop.orders_count)} />
        <Stat label="Note moyenne" value={rating.toFixed(1)} />
        <Stat label="Revenu total" value={fcfa(statistics.total_revenue)} />
        <Stat label="Revenu 30 j" value={fcfa(statistics.monthly_revenue)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Propriétaire & contact">
          <Row label="Responsable" value={owner || "–"} />
          <Row label="Email" value={shop.user?.email} />
          <Row label="Téléphone" value={shop.user?.phone_number} />
          <Row label="WhatsApp" value={shop.whatsapp_number} />
        </Panel>

        <Panel title="Localisation">
          <Row label="Adresse" value={shop.address} />
          <Row label="Ville" value={shop.city} />
          <Row
            label="Coordonnées GPS"
            value={
              shop.latitude && shop.longitude
                ? `${Number(shop.latitude).toFixed(4)}, ${Number(shop.longitude).toFixed(4)}`
                : "Non renseignées"
            }
          />
        </Panel>

        <Panel title="Produits les plus vendus">
          {statistics.top_products?.length ? (
            <div className="divide-y divide-gray-100">
              {statistics.top_products.map((p) => (
                <div key={p.id} className="flex justify-between py-2 text-sm">
                  <span className="text-gray-700 truncate mr-3">{p.name}</span>
                  <span className="text-gray-500 whitespace-nowrap">
                    {fmt(p.quantity_sold || 0)} vendu(s)
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Aucune vente pour le moment.</p>
          )}
        </Panel>

        <Panel title="Commandes récentes">
          {statistics.recent_orders?.length ? (
            <div className="divide-y divide-gray-100">
              {statistics.recent_orders.map((o) => (
                <div key={o.id} className="flex justify-between items-center py-2 text-sm">
                  <div>
                    <div className="text-gray-700">#{o.id}</div>
                    <div className="text-xs text-gray-400">{date(o.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-800">{fcfa(o.total_amount)}</div>
                    <div className="text-xs text-gray-400">{o.status}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Aucune commande récente.</p>
          )}
        </Panel>

        {shop.description && (
          <div className="lg:col-span-2">
            <Panel title="Description">
              <p className="text-sm text-gray-600 leading-relaxed">{shop.description}</p>
            </Panel>
          </div>
        )}
      </div>

      <DeleteConfirmation
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={() => toggleStatusMutation.mutate()}
        title={isActive ? "Fermer la boutique" : "Activer la boutique"}
        message={
          isActive
            ? `Fermer la boutique ${shop.name} ? Elle ne sera plus visible.`
            : `Rendre la boutique ${shop.name} visible pour les clients ?`
        }
        confirmButtonText={isActive ? "Fermer" : "Activer"}
        confirmButtonColor={isActive ? "danger" : "success"}
      />

      <DeleteConfirmation
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onConfirm={() => toggleVerificationMutation.mutate()}
        title={shop.is_verified ? "Retirer la certification" : "Certifier la boutique"}
        message={
          shop.is_verified
            ? `Retirer la certification de ${shop.name} ?`
            : `Certifier ${shop.name} ? Les clients pourront payer en ligne.`
        }
        confirmButtonText={shop.is_verified ? "Retirer" : "Certifier"}
        confirmButtonColor={shop.is_verified ? "danger" : "primary"}
      />
    </div>
  );
};

export default ShopDetail;
